// biome-ignore-all lint/security/noDangerouslySetInnerHtml: the welcome guide is admin-authored and rendered through richTextDisplayHtml (sanitizeRichText whitelist).
// =============================================================================
// ShelterDash.jsx — shelter user's home page ("My Requests")
// =============================================================================
// Renders: an active banners panel, a "New Request" button, and a ReqTable
//          filtered to this shelter's requests. Also surfaces drafts and
//          recent rejection notes inline.
// Props:
//   onNew     - called to open the wizard for a fresh request
//   onSelect  - row click handler
// Context:
//   user         - the logged-in shelter user (drives the filter), from useSession
//   requests     - cached requests list, from useRequests
//   loadRequests - reloads the list (used by the auto-refresh hook), from useRequests
// =============================================================================

import React from "react";
import { useRequests } from "../../app/RequestsContext.jsx";
import { useSession } from "../../app/SessionContext.jsx";
import { getHelpText } from "../../data/helpContent";
import { getWelcomeDismissed, updateProfileById } from "../../data/profiles";
import { listPendingPrefillsForShelter } from "../../data/stockTopUpPrefill";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { richTextDisplayHtml } from "../../services/richText";
import { REQUEST_STATUSES } from "../../utils/enums";
import { Btn, Card } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { HelpButton } from "../form/HelpButton.jsx";
import { ReqTable } from "./ReqTable.jsx";

// Module-level cache so welcome doesn't re-show on ShelterDash remount within a session
const _welcomeDismissedCache = {};

// `canCreate` is the admin "+ New Request" switch (System Setup → Config). When
// off the button is simply HIDDEN — John's call, no explanatory panel. Existing
// drafts are unaffected and can still be finished and submitted.
function ShelterDash({ onNew, onSelect, canCreate = true, onReviewPrefill }) {
  // `user` + `requests` + the list loader now come from context rather than props
  // (Phase 3 refactor).
  const { user } = useSession();
  const { requests, loadRequests } = useRequests();
  const mine = requests || [];

  // Phase 5: pending staff-prepared Stock Top-ups for this service (the in-app half of
  // the notification — the banner below). RLS scopes this to the caller's own service.
  const [pendingTopUps, setPendingTopUps] = React.useState([]);
  React.useEffect(
    function () {
      let alive = true;
      const sid = user?.shelterUuid;
      if (!sid) return undefined;
      listPendingPrefillsForShelter(sid).then(function (rows) {
        if (alive) setPendingTopUps(rows || []);
      });
      return function () {
        alive = false;
      };
    },
    [user?.shelterUuid]
  );

  // Auto-refresh the request list: status changes (approvals, new comments/docs)
  // appear without a manual reload. Polls every 30s while the tab is visible,
  // and instantly when the shelter user returns to the tab.
  useAutoRefresh(function () {
    loadRequests(user);
  });
  const counts = {
    total: mine.length,
    // Stat cards (6): Drafts, Submitted, Part Processed, Active, Fully Processed,
    // Total. "Active" (= Submitted + Part Processed) is a SUBTOTAL shown alongside
    // its two parts, and Total is the grand total — so the cards intentionally
    // overlap (Submitted/Part Processed roll up into Active) rather than partition.
    // The "Active + Drafts" FILTER is broader still (also includes Drafts).
    drafts: mine.filter((r) => r.status === REQUEST_STATUSES.DRAFT).length,
    submitted: mine.filter((r) => r.status === REQUEST_STATUSES.SUBMITTED).length,
    part: mine.filter((r) => r.status === REQUEST_STATUSES.PART_PROCESSED).length,
    active: mine.filter((r) => ["Submitted", "Part Processed"].includes(r.status)).length,
    // The live terminal state is "Fully Processed" (D-1). "Completed" / "Approved"
    // / "Declined" are deliberately LEFT in the background (not surfaced here) —
    // "Completed" is reserved as a possible FUTURE progression after Fully Processed.
    processed: mine.filter((r) => r.status === REQUEST_STATUSES.FULLY_PROCESSED).length,
  };
  const [showWelcome, setShowWelcome] = React.useState(false);
  const [welcomeText, setWelcomeText] = React.useState(null);
  const [savingDismiss, setSavingDismiss] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // Default to "All statuses" (John, 22 Jul) — show everything; the individual
  // statuses + "Active + Drafts" are still selectable in the dropdown.
  const [filterStatus, setFilterStatus] = React.useState("all");

  const filtered = mine.filter(function (r) {
    // "Active" = everything not yet Fully Processed (Draft + Submitted + Part Processed).
    if (
      filterStatus === "active" &&
      ![
        REQUEST_STATUSES.DRAFT,
        REQUEST_STATUSES.SUBMITTED,
        REQUEST_STATUSES.PART_PROCESSED,
      ].includes(r.status)
    )
      return false;
    if (filterStatus === "submitted" && r.status !== REQUEST_STATUSES.SUBMITTED) return false;
    if (filterStatus === "part" && r.status !== REQUEST_STATUSES.PART_PROCESSED) return false;
    if (filterStatus === "processed" && r.status !== REQUEST_STATUSES.FULLY_PROCESSED) return false;
    if (filterStatus === "draft" && r.status !== REQUEST_STATUSES.DRAFT) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !(r.refNo || "").toLowerCase().includes(q) &&
        !(r.shelterRef || "").toLowerCase().includes(q) &&
        !(r.status || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  React.useEffect(
    function () {
      if (!user?.id) return;
      if (_welcomeDismissedCache[user.id]) return;
      getWelcomeDismissed(user.id).then(function (dismissed) {
        if (dismissed) {
          _welcomeDismissedCache[user.id] = true;
          return;
        }
        openGuide();
      });
    },
    [user?.id]
  );

  function openGuide() {
    setShowWelcome(true);
    if (welcomeText !== null) return;
    getHelpText("welcome_overview").then(function (text) {
      setWelcomeText(text ? text : "No content yet.");
    });
  }

  async function dismissForever() {
    setSavingDismiss(true);
    _welcomeDismissedCache[user.id] = true;
    await updateProfileById(user.id, { welcome_dismissed: true });
    setSavingDismiss(false);
    setShowWelcome(false);
  }

  const statCards = [
    { label: "Drafts", value: counts.drafts, colour: "#E65100" },
    { label: "Submitted", value: counts.submitted, colour: "#1565C0" },
    { label: "Part Processed", value: counts.part, colour: "#F9A825" },
    { label: "Active", value: counts.active, colour: "#E65100" },
    { label: "Fully Processed", value: counts.processed, colour: "#388E3C" },
    { label: "Total", value: counts.total, colour: "#00897B" },
  ];

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="m-0 text-[22px] text-zephyr-text">My Requests</h1>
          <p className="mt-1 mb-0 text-zephyr-muted text-[14px]">
            {user.shelterName}
            {user.shelterCode ? ` · ${user.shelterCode}` : ""} - {user.state}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={openGuide}
            className="bg-transparent rounded-[6px] py-[7px] px-[14px] text-[13px] cursor-pointer font-semibold text-zephyr-teal"
            style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
          >
            Quick Start Guide
          </button>
          <HelpButton helpKey="key_contacts" user={user} label="Zephyr Contacts" />
          {canCreate && <Btn onClick={onNew}>+ New Request</Btn>}
        </div>
      </div>
      {pendingTopUps.length > 0 && onReviewPrefill && (
        <Card
          style={{
            marginBottom: 16,
            background: "#E0F2F1",
            border: "1px solid #00897B",
          }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[14px] text-zephyr-teal-dark">
              <strong>📦 Zephyr has prepared a Stock Top-up for you.</strong> Review the order and
              submit it when you're ready.
            </div>
            <Btn onClick={onReviewPrefill}>Review &amp; submit</Btn>
          </div>
        </Card>
      )}
      {showWelcome && (
        <ModalBackdrop
          onClose={function () {
            setShowWelcome(false);
          }}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 28,
            maxWidth: 540,
            width: "calc(100vw - 40px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          <div className="flex justify-between items-center mb-4">
            {/* No help "?" here on purpose: this modal's own body already renders the
                welcome_overview help text (loaded above), so a HelpButton for the same key
                just opened a duplicate copy of this content in a second modal. */}
            <strong className="text-zephyr-teal-dark text-[17px]">
              Welcome to Zephyr Education
            </strong>
            <button
              onClick={function () {
                setShowWelcome(false);
              }}
              className="bg-transparent border-0 text-[20px] cursor-pointer text-[#666]"
            >
              x
            </button>
          </div>
          {welcomeText === null ? (
            <p className="text-zephyr-muted text-[13px]">Loading...</p>
          ) : (
            <div
              className="whitespace-pre-wrap text-[#333] text-[14px] leading-[1.7] m-0 mb-4"
              dangerouslySetInnerHTML={{ __html: richTextDisplayHtml(welcomeText) }}
            />
          )}
          <div className="border-t border-zephyr-border pt-[14px] mt-1 flex flex-col gap-2">
            <p className="text-[12px] text-zephyr-muted m-0 mb-[6px]">
              Other help available on this page:
            </p>
            <div className="bg-zephyr-off-white rounded-[8px] py-[9px] px-[14px] text-[13px] flex items-center gap-2">
              <HelpButton helpKey="key_contacts" user={user} />
              <span>
                <strong className="text-zephyr-teal-dark">Contacts</strong> — Phone, email, postal
                address
              </span>
            </div>
            <div className="bg-zephyr-off-white rounded-[8px] py-[9px] px-[14px] text-[13px] flex items-center gap-2">
              <HelpButton helpKey="request_status" user={user} />
              <span>
                <strong className="text-zephyr-teal-dark">Status guide</strong> — What each request
                status means
              </span>
            </div>
            <div className="bg-zephyr-off-white rounded-[8px] py-[9px] px-[14px] text-[13px] flex items-center gap-2">
              <HelpButton helpKey="what_happens_next" user={user} />
              <span>
                <strong className="text-zephyr-teal-dark">After submitting</strong> — What happens
                after you submit a request
              </span>
            </div>
          </div>
          <button
            onClick={function () {
              setShowWelcome(false);
            }}
            className="mt-5 bg-zephyr-teal-dark text-white border-0 rounded-[8px] py-[10px] px-5 cursor-pointer font-bold text-[14px] w-full"
            style={{ fontFamily: "inherit" }}
          >
            Got it
          </button>
          <button
            onClick={dismissForever}
            disabled={savingDismiss}
            className="mt-2 bg-transparent border-0 text-zephyr-muted text-[12px] cursor-pointer underline w-full"
            style={{ fontFamily: "inherit" }}
          >
            {savingDismiss ? "Saving..." : "Don't show me this again"}
          </button>
        </ModalBackdrop>
      )}

      <div className="grid grid-cols-6 gap-4 mb-5">
        {statCards.map(({ label, value, colour }) => (
          <Card key={label} style={{ padding: "10px 20px" }}>
            <div className="flex items-center justify-center gap-[10px]">
              <div style={{ fontSize: 36, fontWeight: 700, color: colour }}>{value}</div>
              <div
                className={`text-[20px] text-zephyr-muted${label === "Total" ? " font-bold" : ""}`}
              >
                {label}
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div className="py-[14px] px-5 border-b border-zephyr-border flex justify-between items-center flex-wrap gap-[10px]">
          <h2 className="m-0 text-[16px] text-zephyr-text">Request History</h2>
          <div className="flex gap-2 items-center flex-wrap">
            <input
              value={search}
              onChange={function (e) {
                setSearch(e.target.value);
              }}
              placeholder="Search ref or status..."
              className="py-[6px] px-[10px] rounded-[6px] border border-zephyr-border text-[13px] w-[180px]"
              style={{ fontFamily: "inherit" }}
            />
            <select
              value={filterStatus}
              onChange={function (e) {
                setFilterStatus(e.target.value);
              }}
              className="py-[6px] px-[10px] rounded-[6px] border border-zephyr-border text-[13px] bg-white"
              style={{ fontFamily: "inherit" }}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="part">Part Processed</option>
              <option value="active">Active + Drafts</option>
              <option value="processed">Fully Processed</option>
            </select>
            <span className="text-[12px] text-zephyr-muted whitespace-nowrap">
              {filtered.length} request{filtered.length !== 1 ? "s" : ""}
            </span>
            <span className="text-zephyr-muted">|</span>
            <span className="text-[13px] text-zephyr-muted">
              Status guide
              <HelpButton helpKey="request_status" user={user} />
            </span>
            <span className="text-[13px] text-zephyr-muted">
              After submitting
              <HelpButton helpKey="what_happens_next" user={user} />
            </span>
          </div>
        </div>
        <ReqTable
          requests={filtered}
          onSelect={onSelect}
          showShelter={false}
          pinDrafts
          fitViewport
        />
      </Card>
    </div>
  );
}

export { ShelterDash };
